import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../shared';
import { LayoutGrid, Plus, Check, Loader2, X, ChevronDown } from 'lucide-react';

const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const esc = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Ziele, in die eine Markierung gelegt werden kann.
const TARGETS = [
  { key: 'tafel', label: 'Auf die Tafel (Kärtchen)' },
  { key: 'personen', label: 'Bereich: Personen' },
  { key: 'orte', label: 'Bereich: Orte' },
  { key: 'gegenstaende', label: 'Bereich: Gegenstände' },
  { key: 'enzyklopaedie', label: 'Bereich: Enzyklopädie' },
];

// Globale Textmarkierung → „Zu Studienprojekt hinzufügen".
// Funktioniert überall in Scripturia (lauscht auf `selectionchange`, damit die Leiste auch
// dann erscheint, wenn eine Seite/Panel das mouseup-Event stoppt). Ziel wählbar:
// Tafel (Kärtchen) ODER ein konkreter Bereich (Personen/Orte/Gegenstände/Enzyklopädie).
export default function SelectionToProject({ containerSelector = '.scr-ws-main' }) {
  const [sel, setSel] = useState(null); // { text, x, y }
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [newName, setNewName] = useState('');
  const [target, setTarget] = useState('tafel');
  const barRef = useRef(null);
  const openRef = useRef(false);
  useEffect(() => { openRef.current = open; }, [open]);

  const capture = useCallback(() => {
    if (openRef.current) return;
    if (typeof window !== 'undefined' && !localStorage.getItem('token')) { setSel(null); return; }
    const s = window.getSelection();
    if (!s || s.isCollapsed) { setSel(null); return; }
    const text = s.toString().trim();
    if (text.length < 3) { setSel(null); return; }
    const container = containerSelector ? document.querySelector(containerSelector) : document.body;
    const anchor = s.anchorNode;
    const node = anchor && (anchor.nodeType === 3 ? anchor.parentNode : anchor);
    if (node && node.closest && node.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) { setSel(null); return; }
    if (container && node && !container.contains(node)) { setSel(null); return; }
    try {
      const rect = s.getRangeAt(0).getBoundingClientRect();
      if (!rect || (rect.width === 0 && rect.height === 0)) { setSel(null); return; }
      setSel({ text, x: rect.left + rect.width / 2, y: rect.top });
    } catch (e) { setSel(null); }
  }, [containerSelector]);

  useEffect(() => {
    const onUp = () => setTimeout(capture, 10);
    // selectionchange fängt Markierungen auch dann, wenn eine Seite mouseup abfängt.
    let t = null;
    const onSelChange = () => { if (t) clearTimeout(t); t = setTimeout(capture, 180); };
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keyup', onUp);
    document.addEventListener('selectionchange', onSelChange);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keyup', onUp);
      document.removeEventListener('selectionchange', onSelChange);
      if (t) clearTimeout(t);
    };
  }, [capture]);

  useEffect(() => {
    const onDoc = (e) => { if (barRef.current && !barRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openPicker = async () => {
    setOpen(true); setLoading(true); setSaved(null);
    try { const r = await axios.get(`${API_URL}/api/study/boards`, authH()); setBoards(r.data.items || []); } catch (e) {} finally { setLoading(false); }
  };

  const save = async (name) => {
    const nm = (name || '').trim(); if (!nm || !sel) return;
    setSaving(true);
    try {
      if (target === 'tafel') {
        const html = `<div>${esc(sel.text).replace(/\n+/g, '<br/>')}</div>`;
        await axios.post(`${API_URL}/api/study/boards/${encodeURIComponent(nm)}/cards`, { type: 'text', html, color: '#fff7ed' }, authH());
      } else {
        const title = sel.text.replace(/\s+/g, ' ').trim().slice(0, 70);
        await axios.post(`${API_URL}/api/study/boards/${encodeURIComponent(nm)}/areas/${target}`, { title, note: sel.text, source: 'markierung' }, authH());
      }
      setSaved(nm);
      setTimeout(() => { setOpen(false); setSel(null); setSaved(null); setNewName(''); try { window.getSelection().removeAllRanges(); } catch (e) {} }, 1200);
    } catch (e) {} finally { setSaving(false); }
  };

  if (!sel) return null;
  const targetLabel = (TARGETS.find(t => t.key === target) || TARGETS[0]).label;
  const top = Math.max(58, sel.y - 48);
  const left = Math.min(Math.max(120, sel.x), (typeof window !== 'undefined' ? window.innerWidth : 1200) - 160);

  return (
    <div ref={barRef} data-testid="sel-to-project" style={{ position: 'fixed', top, left, transform: 'translateX(-50%)', zIndex: 2147483000 }}>
      {!open ? (
        <button data-testid="sel-to-project-btn" onMouseDown={(e) => e.preventDefault()} onClick={openPicker}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#D8B76A,#B6974F)', color: '#15100a', border: 'none', borderRadius: 999, padding: '8px 15px', fontSize: 13, fontWeight: 800, cursor: 'pointer', boxShadow: '0 10px 26px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>
          <LayoutGrid size={15} /> Zu Studienprojekt
        </button>
      ) : (
        <div data-testid="sel-to-project-panel" onMouseDown={(e) => e.stopPropagation()}
          style={{ width: 284, background: 'var(--scr-surface,#fff)', border: '1px solid var(--scr-border,rgba(0,0,0,0.14))', borderRadius: 14, boxShadow: '0 20px 50px rgba(0,0,0,0.3)', padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--scr-faint,#777)', flex: 1 }}>Markierung in Projekt legen</span>
            <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--scr-faint,#999)', padding: 2 }}><X size={15} /></button>
          </div>
          <div style={{ fontSize: 12, color: 'var(--scr-text-2,#555)', background: 'var(--scr-gold-soft,#faf6ec)', border: '1px solid var(--scr-border-2,rgba(0,0,0,0.08))', borderRadius: 10, padding: '7px 10px', marginBottom: 10, maxHeight: 60, overflow: 'hidden' }}>
            „{sel.text.slice(0, 120)}{sel.text.length > 120 ? '…' : ''}"
          </div>
          {saved ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: '#0d9488', fontSize: 13, fontWeight: 700, padding: '8px 4px' }}><Check size={16} /> In „{saved}" ({targetLabel.replace('Bereich: ', '')}) gespeichert</div>
          ) : (
            <>
              {/* Ziel wählen: Tafel oder konkreter Bereich */}
              <div style={{ position: 'relative', marginBottom: 10 }}>
                <select data-testid="sel-to-project-target" value={target} onChange={(e) => setTarget(e.target.value)}
                  style={{ width: '100%', boxSizing: 'border-box', appearance: 'none', background: 'var(--scr-surface-2,#f6f5f3)', border: '1px solid var(--scr-border-2,rgba(0,0,0,0.14))', borderRadius: 999, padding: '8px 30px 8px 12px', fontSize: 12.5, fontWeight: 700, color: 'var(--scr-text,#222)', outline: 'none', cursor: 'pointer' }}>
                  {TARGETS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--scr-faint,#999)' }} />
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: boards.length ? 10 : 0 }}>
                <input data-testid="sel-to-project-new-input" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(newName); }} placeholder="Neues Projekt…"
                  style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', background: 'var(--scr-surface-2,#f6f5f3)', border: '1px solid var(--scr-border-2,rgba(0,0,0,0.14))', borderRadius: 999, padding: '7px 12px', fontSize: 12.5, color: 'var(--scr-text,#222)', outline: 'none' }} />
                <button data-testid="sel-to-project-new-save" onClick={() => save(newName)} disabled={saving || !newName.trim()}
                  style={{ background: '#B6974F', color: '#fff', border: 'none', borderRadius: 999, padding: '7px 11px', fontSize: 12.5, fontWeight: 800, cursor: newName.trim() ? 'pointer' : 'default', opacity: newName.trim() ? 1 : 0.6, display: 'inline-flex', alignItems: 'center' }}>{saving ? <Loader2 size={13} className="gs-spin" /> : <Plus size={13} />}</button>
              </div>
              {loading ? <div style={{ fontSize: 12, color: 'var(--scr-faint,#999)', padding: '6px 4px' }}>Lädt…</div> : boards.length > 0 ? (
                <div style={{ maxHeight: 190, overflowY: 'auto', display: 'grid', gap: 2 }}>
                  {boards.map((b) => (
                    <button key={b.name} data-testid="sel-to-project-option" onClick={() => save(b.name)} disabled={saving}
                      style={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, background: 'transparent', border: 'none', borderRadius: 8, padding: '8px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--scr-text,#222)' }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--scr-gold-soft,#f1efeb)'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <LayoutGrid size={14} color="#B6974F" /> <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--scr-faint,#999)' }}>{b.cards}</span>
                    </button>
                  ))}
                </div>
              ) : !loading && <div style={{ fontSize: 12, color: 'var(--scr-faint,#999)', padding: '4px 2px' }}>Noch keine Projekte – lege oben eins an.</div>}
            </>
          )}
        </div>
      )}
      <style>{`@keyframes gs-spin { to { transform: rotate(360deg); } } .gs-spin { animation: gs-spin 0.9s linear infinite; }`}</style>
    </div>
  );
}
